import { DropEvent, useDropzone } from 'react-dropzone';
import Iconify from '@emfood/shared/src/components/iconify';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import FormHelperText from '@mui/material/FormHelperText';

import { varAlpha } from 'src/theme/styles';

import type { UploadProps } from './types';
import { UploadPlaceholder } from './components/placeholder';
import { RejectionFiles } from './components/rejection-files';
import { MultiFilePreview } from './components/preview-multi-file';
import { DeleteButton, SingleFilePreview } from './components/preview-single-file';

// ----------------------------------------------------------------------

export function Upload({
  sx,
  value,
  error,
  disabled,
  onDelete,
  onUpload,
  onRemove,
  thumbnail,
  helperText,
  onRemoveAll,
  multiple = false,
  ...other
}: UploadProps) {
  const { getRootProps, getInputProps, isDragActive, isDragReject, fileRejections } = useDropzone({
    multiple,
    disabled,
    getFilesFromEvent: (event) => uuidFileGetter(event, multiple),
    ...other,
  });

  const isArray = Array.isArray(value) && multiple;

  const hasFile = !isArray && !!value;

  const hasFiles = isArray && !!value.length;

  const hasError = isDragReject || !!error;

  const renderMultiPreview = hasFiles && (
    <>
      <MultiFilePreview files={value} thumbnail={thumbnail} onRemove={onRemove} sx={{ my: 3 }} />

      {(onRemoveAll || onUpload) && (
        <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
          {onRemoveAll && (
            <Button color="inherit" variant="outlined" size="small" onClick={onRemoveAll}>
              Remove all
            </Button>
          )}

          {onUpload && (
            <Button
              size="small"
              variant="contained"
              onClick={onUpload}
              startIcon={<Iconify icon="eva:cloud-upload-fill" />}
            >
              Upload
            </Button>
          )}
        </Stack>
      )}
    </>
  );

  return (
    <Box sx={{ width: 1, position: 'relative', ...sx }}>
      <Box
        {...getRootProps()}
        sx={{
          p: 5,
          outline: 'none',
          borderRadius: 1,
          cursor: 'pointer',
          overflow: 'hidden',
          position: 'relative',
          bgcolor: (theme) => varAlpha(theme.palette.grey['500'], 0.08),
          border: (theme) => `1px dashed ${varAlpha(theme.palette.grey['500'], 0.2)}`,
          transition: (theme) => theme.transitions.create(['opacity', 'padding']),
          '&:hover': { opacity: 0.72 },
          ...(isDragActive && { opacity: 0.72 }),
          ...(disabled && { opacity: 0.48, pointerEvents: 'none' }),
          ...(hasError && {
            color: 'error.main',
            borderColor: 'error.main',
            bgcolor: (theme) => varAlpha(theme.palette.error.main, 0.08),
          }),
          ...(hasFile && { padding: '28% 0' }),
        }}
      >
        <input {...getInputProps()} />

        {/* Single file */}
        {hasFile ? <SingleFilePreview file={value as File} /> : <UploadPlaceholder />}
      </Box>

      {/* Single file */}
      {hasFile && <DeleteButton onClick={onDelete} />}

      {helperText && (
        <FormHelperText error={!!error} sx={{ px: 2 }}>
          {helperText}
        </FormHelperText>
      )}

      <RejectionFiles files={[...fileRejections]} />

      {/* Multi files */}
      {renderMultiPreview}
    </Box>
  );
}

// Adds a UUID to the file name. To solve the issue of non-unique React keys when rendering file previews, and to prevent special characters from being inserted into file names.
async function uuidFileGetter(event: DropEvent, multiple: boolean) {
  const files = [];
  const fileList = (event as React.DragEvent<HTMLElement>).dataTransfer
    ? (event as React.DragEvent<HTMLElement>).dataTransfer.files
    : (event as React.ChangeEvent<HTMLInputElement>).target.files;

  if (fileList) {
    for (let i = 0; i < fileList.length; i += 1) {
      const file = fileList[i];

      let newFileName = '';
      if (multiple) {
        newFileName = `${crypto.randomUUID()}-${file.name
          .replace(/[^a-zA-Z0-9.\-_]/g, '_') // Replace special chars with underscore
          .replace(/_+/g, '_')}`;
      } else {
        newFileName = `${file.name
          .replace(/[^a-zA-Z0-9.\-_]/g, '_') // Replace special chars with underscore
          .replace(/_+/g, '_')}`;
      }

      const newFile = new File([file], newFileName, { type: file.type });
      // Restore the `path` react-dropzone normally attaches — the preview's file-format
      // detection reads it, and without it image thumbnails fall back to a broken icon.
      Object.assign(newFile, { path: newFileName });

      files.push(newFile);
    }
  }

  return files;
}
